import type { PayRunCardDto } from './payRunsApiTypes';
import type { PayRunProgressItem } from './PayRunProgressList';

const currency = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
const date = new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });

export const formatSystemValue = (value: string): string => {
  const words = value.replace(/_/g, ' ');
  return words ? `${words[0].toUpperCase()}${words.slice(1)}` : '';
};

export const formatPayRunDate = (value: string): string => {
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? value : date.format(parsed);
};

const calculation = (value: number): string => currency.format(value);

const operatingStateLabel = (value: string): string => {
  switch (value) {
    case 'trial_pay': return 'Trial pay';
    case 'payroll_active': return 'Active pay';
    case 'cancelled': return 'Cancelled';
    default: return 'Status unavailable';
  }
};

export const isKnownWorkflowStatus = (value: string): boolean =>
  ['open', 'compliance', 'ready_to_pay', 'eft_ready', 'complete', 'cancelled'].includes(value);

export const isKnownOperatingState = (value: string): boolean =>
  ['trial_pay', 'payroll_active', 'cancelled'].includes(value);

export const isSafePayRunPresentationState = (card: Pick<PayRunCardDto, 'workflowStatus' | 'operatingState'>): boolean =>
  isKnownWorkflowStatus(card.workflowStatus)
  && (card.workflowStatus === 'cancelled'
    ? card.operatingState === 'cancelled' || card.operatingState === 'payroll_active'
    : card.operatingState === 'trial_pay' || card.operatingState === 'payroll_active');

export const buildPayRunCardModel = (card: PayRunCardDto) => ({
  id: card.payId,
  title: card.displayTitle,
  businessRecordName: card.businessRecordName,
  payGroupName: card.payGroupName,
  frequencyValue: card.frequency,
  frequencyLabel: formatSystemValue(card.frequency),
  periodLabel: card.payPeriodLabel,
  periodRange: `${formatPayRunDate(card.periodStartDate)} - ${formatPayRunDate(card.periodEndDate)}`,
  paymentDate: formatPayRunDate(card.paymentDate),
  runTypeValue: card.runType,
  runTypeLabel: formatSystemValue(card.runType),
  operatingStateValue: card.operatingState,
  operatingStateLabel: card.workflowStatus === 'cancelled' ? 'Cancelled' : operatingStateLabel(card.operatingState),
  workflowStatusValue: card.workflowStatus,
  workflowStatusLabel: formatSystemValue(card.workflowStatus),
  facets: card.statusFacets.map((facet) => ({
    type: facet.type,
    value: facet.status,
    label: `${formatSystemValue(facet.type)}: ${formatSystemValue(facet.status)}`,
    detail: facet.detail,
    blockingCount: facet.blockingCount ?? 0,
    warningCount: facet.warningCount ?? 0,
  })),
  gross: card.calculation?.status === 'complete' ? calculation(card.gross) : '—',
  tax: card.calculation?.status === 'complete' ? calculation(card.tax) : '—',
  stsl: card.calculation?.status === 'complete' ? calculation(card.stsl) : '—',
  super: card.calculation?.status === 'complete' ? calculation(card.super) : '—',
  netPay: card.calculation?.status === 'complete' ? calculation(card.netPay) : '—',
  employeeCount: card.employeeCount,
  actions: card.meta.actions,
});

const PROGRESS_STAGES = [
  { id: 'pay_period', label: 'Pay period' },
  { id: 'review', label: 'Review' },
  { id: 'ready_to_pay', label: 'Ready to pay' },
  { id: 'approval', label: 'Approval' },
  { id: 'payment', label: 'Payment' },
  { id: 'close', label: 'Close' },
] as const;

export const resolvePayRunLifecycleStageIndex = (
  workflowStatus: string,
  approval?: Pick<NonNullable<PayRunCardDto['approval']>, 'submittedAt' | 'isSuperseded' | 'hasRejectedDecision'> | null,
): number => {
  switch (workflowStatus) {
    case 'open': return 1;
    case 'compliance': return 1;
    case 'ready_to_pay':
      return approval?.submittedAt && !approval.isSuperseded && !approval.hasRejectedDecision ? 3 : 2;
    case 'eft_ready': return 4;
    case 'complete': return 5;
    default: return -1;
  }
};

export const buildPayRunProgressItems = (
  workflowStatus: string,
  payId: string,
  approval?: PayRunCardDto['approval'],
  operatingState?: string,
): PayRunProgressItem[] => {
  const currentIndex = operatingState !== undefined && !isKnownOperatingState(operatingState)
    ? -1
    : resolvePayRunLifecycleStageIndex(workflowStatus, approval);
  const href = `/payroll/pay-runs/${encodeURIComponent(payId)}`;
  return PROGRESS_STAGES.map((stage, index) => {
    const state = currentIndex < 0 || index > currentIndex
      ? 'upcoming'
      : index === currentIndex ? 'current' : 'complete';
    return {
      id: stage.id,
      label: stage.label,
      state,
      href: state === 'upcoming' ? undefined : href,
    };
  });
};

export interface PayRunCardAction {
  label: string;
  href: string;
  variant: 'primary' | 'secondary';
  icon?: 'status-caution';
}

export const buildPayRunCardAction = (card: PayRunCardDto): PayRunCardAction | null => {
  const detailHref = `/payroll/pay-runs/${encodeURIComponent(card.payId)}`;
  if (!isSafePayRunPresentationState(card)) return null;
  if (card.workflowStatus === 'ready_to_pay' && card.approval?.canSubmit)
    return { label: 'Submit for approval', href: detailHref, variant: 'primary' };
  if (card.workflowStatus === 'ready_to_pay' && card.approval?.submittedAt && !card.approval.isSuperseded)
    return { label: 'View approval progress', href: detailHref, variant: 'secondary' };
  if (card.workflowStatus === 'compliance')
    return { label: 'Review Compliance', href: `${detailHref}?tab=exceptions`, variant: 'secondary', icon: 'status-caution' };
  return { label: 'Open pay run', href: detailHref, variant: 'secondary' };
};
